import { FC, useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {FlexBox, FInter, Avatar, useAdvancedLockBodyScroll, useBreakpoint, CommonInput, INPUT_TYPES} from '@brg/ui';
import {CloseIcon} from 'shared/icons';
import SearchIcon16 from 'shared/icons/SearchIcon16';
import {
    SearchInputsWrapper,
    SearchInput,
    ResultsWrapper,
    ResultsItem,
    MobileSearchBarWrapper,
    BackdropContainer, CloseIconWrapper, SearchBarOutside, ProjectImageWrapper
} from './styles';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { computeDesigner } from 'shared/helpers/computeData';
import { useDebouncedEffect } from '../__FOX__/NAV/src/widgets/navbar-search-widget';
import {DesignerAPI, Designer, API, TeamAPI, ProjectAPI, Project} from '@brg/api';
import { CreateLinkTo } from 'shared/constants/linkTo';
import { useHistory } from 'react-router-dom';
import { computeProject } from 'shared/helpers/types/computed/ComputedProject';
import { LayoutConst } from 'core';

interface SearchProps {
    placeholder: string;
    teamId?: string;
    competitionId?: string;
    isCgRave?: boolean;
    customStyles?: any;
    disabledLock?: boolean;
    onCustomSearch?: (data: string) => void;
}

const MobileSearchBar: FC<SearchProps> = ({ placeholder, teamId = '', competitionId = '', customStyles, disabledLock, isCgRave, onCustomSearch }) => {
    const theme = useTheme();
    const history = useHistory();
    const { t, i18n } = useTranslation();
    const resultsRef = useRef<any>();

    const [searchText, setSearchText] = useState("");
    const [isEmpty, setIsEmpty] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const is743 = useBreakpoint(LayoutConst.screen.s - 1);

    const [lockBody, unlockBody] = useAdvancedLockBodyScroll();

    const cgRaveFilter = {competitionId: competitionId, requestType: 3, ignoreContext: true}
    const isProject = competitionId !== '' && !isCgRave

    const [items, setItems] = useState<Designer[] | Project[]>([]);

        useDebouncedEffect(
            () => {
              if (searchText.length >= 2) {
                if (onCustomSearch) {
                    onCustomSearch(searchText)
                    return;
                }

                return (teamId ? TeamAPI.AutoComplete({ key: searchText?.trim(), teamId: teamId })
                    : isProject ? ProjectAPI.AutoComplete({ key: searchText?.trim(), competitionIds: [competitionId] })
                    : DesignerAPI.AutoComplete({ key: searchText?.trim(), ...(isCgRave ? cgRaveFilter : {})}))
                    //@ts-ignore
                  .then((v) => v.json())
                  .then((v) => {
                    return isProject ? API.toLang<Project[]>(v, i18n.language) : API.toLang<Designer[]>(v, i18n.language)
                  })
                  .then((v) => {
                    setItems(v)
                    setIsEmpty(v.length < 1);
                  });
              } else {
                setItems([]);
                onCustomSearch && onCustomSearch('')
              }
            },
            [searchText, i18n.language, teamId, onCustomSearch],
            700
          );

          const lockHandlers = useMemo(() => {
            if (!disabledLock) {
                return {
                    onMouseEnter: () => lockBody(),
                    onMouseLeave: () => unlockBody()
                }
            }
          }, [disabledLock, unlockBody, lockBody])

      const onSearchResultItemClick = useCallback((id: string) => {
        isProject
            ? window.open(CreateLinkTo.project(id), '_BLANK') 
            : history.push(CreateLinkTo.designer(id))
      }, [history, isProject]);

      useEffect(() => {

          if (searchText?.length > 0 && !onCustomSearch) setIsOpen(true);
          else setIsOpen(false)

      }, [searchText, onCustomSearch])

      useEffect(() => {
        const handleOutsideClick = (e: any) => {
            setIsOpen(false)
            unlockBody()
        }
        const searchBarOutside = document.querySelector("#searchBarOutside")

        searchBarOutside?.addEventListener("click", handleOutsideClick)

        return () => {
            searchBarOutside?.removeEventListener("click", handleOutsideClick)
        }
      }, [isOpen])

    return (
        <MobileSearchBarWrapper>
           {isOpen && <SearchBarOutside id='searchBarOutside'></SearchBarOutside>}
          <BackdropContainer maxHeight={searchText.length > 0 && isOpen} style={{ zIndex: isProject ? 0 : 9998}}>
            <CommonInput 
                type={INPUT_TYPES.search}
                value={searchText}
                //@ts-ignore
                onChange={(e) => setSearchText(e.target.value)}
                placeholder={placeholder}
            />

            {(searchText.length > 0 && isOpen) ? (
                <ResultsWrapper className={'results-wrapper'} {...lockHandlers}  ref={resultsRef} style={{ top: customStyles?.top ? customStyles?.top : 'initial' }}>
                    {items.map((v) => {
                    const item = isProject ? v as Project : v as Designer;
                    const cv = computeDesigner(item);
                    const project = computeProject(item);

                    if (isProject) {
                        return (
                            <ResultsItem
                                onClick={() => onSearchResultItemClick(project.projectId)}
                                key={project.projectId}
                            >
                                <FlexBox align={"center"} gx={8}>
                                    <FlexBox style={{ padding: "4px", borderRadius: '30px' }} width={64} height={64}>
                                        <ProjectImageWrapper files={project?.titleImages} />
                                    </FlexBox>
                                    <FInter.SmallTextDiv color={theme.mobileSearch.item.color}>{project?.title}</FInter.SmallTextDiv>
                                </FlexBox>
                            </ResultsItem>
                        )
                    }
                    return (
                        <ResultsItem
                        onClick={() => onSearchResultItemClick(cv.accountId)}
                        key={cv.accountId}
                        >
                        <FlexBox align={"center"} gx={8}>
                            <FlexBox style={{ padding: "4px", borderRadius: '30px' }} width={64} height={64}>
                            <Avatar {...cv} rounded />
                            </FlexBox>
                            <FInter.SmallTextDiv color={theme.mobileSearch.item.color}>{cv.fullName}</FInter.SmallTextDiv>
                        </FlexBox>
                        </ResultsItem>
                    );
                    })}
                    {items.length < 1 && searchText.length < 2 && (<ResultsItem>
                        <FlexBox
                        align={"center"}
                        width={"100%"}
                        justify={"center"}
                        gx={8}
                        >
                        <FInter.SmallTextDiv color={theme.mobileSearch.item.color}>
                            {t("text.more2chars")}
                        </FInter.SmallTextDiv>
                        </FlexBox>
                    </ResultsItem>
                    )}
                    {items.length < 1 && searchText.length >= 2 && !isEmpty && (
                    <ResultsItem>
                        <FlexBox
                        align={"center"}
                        width={"100%"}
                        justify={"center"}
                        gx={8}
                        >
                        <FInter.SmallTextDiv color={theme.mobileSearch.item.color}>{t("text.loading")}</FInter.SmallTextDiv>
                        </FlexBox>
                    </ResultsItem>
                    )}
                    {items.length < 1 && searchText.length >= 2 && isEmpty && (
                    <ResultsItem>
                        <FlexBox
                        align={"center"}
                        width={"100%"}
                        justify={"center"}
                        gx={8}
                        >
                        <FInter.SmallTextDiv color={theme.mobileSearch.item.color}>
                            {t("text.notfound")}
                        </FInter.SmallTextDiv>
                        </FlexBox>
                    </ResultsItem>
                    )}
                </ResultsWrapper>
            ) : null}
                            </BackdropContainer>
        </MobileSearchBarWrapper>
    )
}

export default MobileSearchBar;import { FC, useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {FlexBox, FInter, Avatar, useAdvancedLockBodyScroll, useBreakpoint, CommonInput, INPUT_TYPES} from '@brg/ui';
import {CloseIcon} from 'shared/icons';
import SearchIcon16 from 'shared/icons/SearchIcon16';
import {
    SearchInputsWrapper,
    SearchInput,
    ResultsWrapper,
    ResultsItem,
    MobileSearchBarWrapper,
    BackdropContainer, CloseIconWrapper, SearchBarOutside, ProjectImageWrapper
} from './styles';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { computeDesigner } from 'shared/helpers/computeData';
import { useDebouncedEffect } from '../__FOX__/NAV/src/widgets/navbar-search-widget';
import {DesignerAPI, Designer, API, TeamAPI, ProjectAPI, Project} from '@brg/api';
import { CreateLinkTo } from 'shared/constants/linkTo';
import { useHistory } from 'react-router-dom';
import { computeProject } from 'shared/helpers/types/computed/ComputedProject';
import { LayoutConst } from 'core';

interface SearchProps {
    placeholder: string;
    teamId?: string;
    competitionId?: string;
    isCgRave?: boolean;
    customStyles?: any;
    disabledLock?: boolean;
    onCustomSearch?: (data: string) => void;
}

const MobileSearchBar: FC<SearchProps> = ({ placeholder, teamId = '', competitionId = '', customStyles, disabledLock, isCgRave, onCustomSearch }) => {
    const theme = useTheme();
    const history = useHistory();
    const { t, i18n } = useTranslation();
    const resultsRef = useRef<any>();

    const [searchText, setSearchText] = useState("");
    const [isEmpty, setIsEmpty] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const is743 = useBreakpoint(LayoutConst.screen.s - 1);

    const [lockBody, unlockBody] = useAdvancedLockBodyScroll();

    const cgRaveFilter = {competitionId: competitionId, requestType: 3, ignoreContext: true}
    const isProject = competitionId !== '' && !isCgRave

    const [items, setItems] = useState<Designer[] | Project[]>([]);

        useDebouncedEffect(
            () => {
              if (searchText.length >= 2) {
                if (onCustomSearch) {
                    onCustomSearch(searchText)
                    return;
                }

                return (teamId ? TeamAPI.AutoComplete({ key: searchText?.trim(), teamId: teamId })
                    : isProject ? ProjectAPI.AutoComplete({ key: searchText?.trim(), competitionIds: [competitionId] })
                    : DesignerAPI.AutoComplete({ key: searchText?.trim(), ...(isCgRave ? cgRaveFilter : {})}))
                    //@ts-ignore
                  .then((v) => v.json())
                  .then((v) => {
                    return isProject ? API.toLang<Project[]>(v, i18n.language) : API.toLang<Designer[]>(v, i18n.language)
                  })
                  .then((v) => {
                    setItems(v)
                    setIsEmpty(v.length < 1);
                  });
              } else {
                setItems([]);
                onCustomSearch && onCustomSearch('')
              }
            },
            [searchText, i18n.language, teamId, onCustomSearch],
            700
          );

          const lockHandlers = useMemo(() => {
            if (!disabledLock) {
                return {
                    onMouseEnter: () => lockBody(),
                    onMouseLeave: () => unlockBody()
                }
            }
          }, [disabledLock, unlockBody, lockBody])

      const onSearchResultItemClick = useCallback((id: string) => {
        isProject
            ? window.open(CreateLinkTo.project(id), '_BLANK') 
            : history.push(CreateLinkTo.designer(id))
      }, [history, isProject]);

      useEffect(() => {

          if (searchText?.length > 0 && !onCustomSearch) setIsOpen(true);
          else setIsOpen(false)

      }, [searchText, onCustomSearch])

      useEffect(() => {
        const handleOutsideClick = (e: any) => {
            setIsOpen(false)
            unlockBody()
        }
        const searchBarOutside = document.querySelector("#searchBarOutside")

        searchBarOutside?.addEventListener("click", handleOutsideClick)

        return () => {
            searchBarOutside?.removeEventListener("click", handleOutsideClick)
        }
      }, [isOpen])

    return (
        <MobileSearchBarWrapper>
           {isOpen && <SearchBarOutside id='searchBarOutside'></SearchBarOutside>}
          <BackdropContainer maxHeight={searchText.length > 0 && isOpen} style={{ zIndex: isProject ? 0 : 9998}}>
            <CommonInput 
                type={INPUT_TYPES.search}
                value={searchText}
                //@ts-ignore
                onChange={(e) => setSearchText(e.target.value)}
                placeholder={placeholder}
            />

            {(searchText.length > 0 && isOpen) ? (
                <ResultsWrapper className={'results-wrapper'} {...lockHandlers}  ref={resultsRef} style={{ top: customStyles?.top ? customStyles?.top : 'initial' }}>
                    {items.map((v) => {
                    const item = isProject ? v as Project : v as Designer;
                    const cv = computeDesigner(item);
                    const project = computeProject(item);

                    if (isProject) {
                        return (
                            <ResultsItem
                                onClick={() => onSearchResultItemClick(project.projectId)}
                                key={project.projectId}
                            >
                                <FlexBox align={"center"} gx={8}>
                                    <FlexBox style={{ padding: "4px", borderRadius: '30px' }} width={64} height={64}>
                                        <ProjectImageWrapper files={project?.titleImages} />
                                    </FlexBox>
                                    <FInter.SmallTextDiv color={theme.mobileSearch.item.color}>{project?.title}</FInter.SmallTextDiv>
                                </FlexBox>
                            </ResultsItem>
                        )
                    }
                    return (
                        <ResultsItem
                        onClick={() => onSearchResultItemClick(cv.accountId)}
                        key={cv.accountId}
                        >
                        <FlexBox align={"center"} gx={8}>
                            <FlexBox style={{ padding: "4px", borderRadius: '30px' }} width={64} height={64}>
                            <Avatar {...cv} rounded />
                            </FlexBox>
                            <FInter.SmallTextDiv color={theme.mobileSearch.item.color}>{cv.fullName}</FInter.SmallTextDiv>
                        </FlexBox>
                        </ResultsItem>
                    );
                    })}
                    {items.length < 1 && searchText.length < 2 && (<ResultsItem>
                        <FlexBox
                        align={"center"}
                        width={"100%"}
                        justify={"center"}
                        gx={8}
                        >
                        <FInter.SmallTextDiv color={theme.mobileSearch.item.color}>
                            {t("text.more2chars")}
                        </FInter.SmallTextDiv>
                        </FlexBox>
                    </ResultsItem>
                    )}
                    {items.length < 1 && searchText.length >= 2 && !isEmpty && (
                    <ResultsItem>
                        <FlexBox
                        align={"center"}
                        width={"100%"}
                        justify={"center"}
                        gx={8}
                        >
                        <FInter.SmallTextDiv color={theme.mobileSearch.item.color}>{t("text.loading")}</FInter.SmallTextDiv>
                        </FlexBox>
                    </ResultsItem>
                    )}
                    {items.length < 1 && searchText.length >= 2 && isEmpty && (
                    <ResultsItem>
                        <FlexBox
                        align={"center"}
                        width={"100%"}
                        justify={"center"}
                        gx={8}
                        >
                        <FInter.SmallTextDiv color={theme.mobileSearch.item.color}>
                            {t("text.notfound")}
                        </FInter.SmallTextDiv>
                        </FlexBox>
                    </ResultsItem>
                    )}
                </ResultsWrapper>
            ) : null}
                            </BackdropContainer>
        </MobileSearchBarWrapper>
    )
}

export default MobileSearchBar;